// The ONE transport for the `/api/models` route family — `modelApi.ts` (the model record) and
// `mdrApi.ts` (the MDR ledger) both ride it.
//
// WHY IT EXISTS (B-3, 2026-08-23). The two clients had grown a fetch discipline each: two
// `AbortController` timeouts, two `credentials: 'same-origin'` spreads, two `GET`-by-default
// spreads, two closed-vocabulary error decoders, two `empty_response_body` guards, and two
// structurally identical error classes. They served the SAME origin, the SAME base path and the
// SAME ADR-043 error envelope, so the duplication bought nothing and cost drift: the model half
// was fixed to carry `error_args` while the MDR half was not, and the two would have kept
// diverging one review finding at a time.
//
// ⚠️ WHAT IS **NOT** HERE, AND WHY — the assumption this transport makes that does not carry
// everywhere. Every function below assumes its target is a **same-origin Bennie route answering
// the ADR-043 `{ error, error_args }` envelope under a bounded read timeout.** `uploadMdrVersion`'s
// blob PUT satisfies none of the three: it addresses an Azure Storage SAS on a foreign origin, it
// answers with Azure's XML error body and no `error` code to decode, and a 30-second abort would
// kill a legitimate large upload mid-flight. It therefore keeps its own bespoke `fetch` and says so
// at the site. Forcing it through here would have meant inventing an error code it never sends —
// per core-principles, that is copying a contract, not adopting one.
//
// ⛔ This is the model-registry family's transport, NOT a repo-wide one. `views/reggie/reggieWsApi.ts`
// and `views/toolbox/toolboxApi.ts` carry their own near-identical fetch disciplines against
// different bases and different error classes; converging those is a wider change than B-3 and is
// recorded as a finding rather than done here.

import type { ApiErrorPayload } from '../../lib/i18nResolve';

/** The base every route in this family hangs off. No trailing slash. */
export const MODEL_API_BASE = '/api/models';

/** ➕ WI-V4 — the taxonomy base, for CK-published vocabularies that are not model-scoped. No
 * trailing slash. `/api/taxonomy/severity` is the sibling this route was modelled on. */
export const TAXONOMY_API_BASE = '/api/taxonomy';

/** Never wait indefinitely — frontend-rules Streaming/State. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * ➕ **WI-63 — the budget for a call that reads a whole document through a model.**
 *
 * 🔴 **DERIVED FROM THE SERVER'S OWN BUDGET, NOT PICKED.** `appsettings.json` declares
 * `Agents:MdrParseTimeoutSeconds: 300`, armed at the dispatch via `CreateBudgetedCts`. A client
 * abort that fires FIRST turns a parse the server is still running into a `request_timeout` on
 * screen while the model call keeps billing — so the client bound must sit ABOVE the server's, not
 * below it. The margin covers the request's own non-model legs (blob read, provenance write,
 * fact writes, the refetched block).
 *
 * ⛔ **`DEFAULT_TIMEOUT_MS` IS NOT USABLE HERE AND THE DIFFERENCE IS NOT A TUNING PREFERENCE.** At
 * 30 s a parse caller aborts EVERY real parse — the failure is total, not marginal, and it would
 * surface as a timeout the server never saw.
 *
 * ⚠️ The locked mock's soft budget is 3 minutes (*"at 3 minutes an offer to keep waiting or to
 * register the model without the parse"*). That is a NOTICE threshold, not an abort: the two are
 * different numbers doing different jobs, and collapsing them would abort a run the design says to
 * offer to keep waiting for.
 */
export const PARSE_TIMEOUT_MS = 330_000;

/**
 * ➕ **WI-63 / `D6` — how a CANCELLED request differs from a TIMED-OUT one, at the transport.**
 *
 * 🔴 **THEY ARRIVE AS THE SAME `AbortError` AND THEY ARE OPPOSITE FACTS.** A timeout is the client
 * giving up on a server that may still be working; a cancel is the USER stopping work they no
 * longer want. `D6` makes the distinction a backend requirement rather than a UI nicety — *"if user
 * cancels out mid-flight, the cancel should send a TERMINATION SIGNAL to the endpoint to preserve
 * token"* — and the server already honours it (`RequestAborted → ct → AnthropicProvider`). What was
 * missing is that the CLIENT could not tell the two apart, so a cancel and a timeout would have
 * rendered the same sentence and offered the same control.
 *
 * Pass a caller-owned signal and an abort THROUGH it is reported as `request_cancelled`; the
 * internal deadline still reports `request_timeout`. When both have fired the CANCEL wins, because
 * the user's action is the one they are owed an account of.
 */
export type RegistryFetchInit = Pick<RequestInit, 'method' | 'headers' | 'body'> & {
  /** Caller-owned cancellation. Aborting it terminates the in-flight request server-side too. */
  signal?: AbortSignal;
};

/**
 * A typed model-registry failure. `code` is the backend's closed-vocabulary error code
 * (`model_not_found`, `mdr_version_not_promotable`, …); `status` the HTTP status; `error_args` the
 * ADR-043 structured-args block that the authored copy for that code interpolates.
 *
 * The UI renders a message resolved from the code, NEVER the code, the status, or the raw body —
 * frontend-rules Error Display forbids leaking internal service names or config detail into
 * user-visible output.
 *
 * 🔴 **`error_args` IS LOAD-BEARING, NOT DIAGNOSTIC PADDING.** `errors.json` authors this family's
 * copy WITH interpolation slots — `mdr_version_not_promotable` is *"A documentation version with
 * status '{{status}}' can't be approved."* and `empty_patch` is *"No changes were provided.
 * Editable fields: {{mutable}}."* `resolveApiError` feeds the args to i18next as
 * `i18n.t(key, payload.error_args ?? {})`, so an error that arrives here with the args dropped
 * renders the literal `{{status}}` to the user. Both former decoders read `body.error` and threw
 * the args away; carrying them is the whole reason this class has a third field.
 */
export class ModelApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** The ADR-043 args block. `{}` when the server sent none — which is what it sends for most codes. */
  readonly error_args: Record<string, unknown>;
  constructor(code: string, status: number, errorArgs: Record<string, unknown> = {}) {
    super(`model_api_error:${code}:${status}`);
    this.name = 'ModelApiError';
    this.code = code;
    this.status = status;
    this.error_args = errorArgs;
  }
}

/**
 * One bounded, credentialed request against the family's base.
 *
 * `path` is `''` for the collection and `/<...>` for anything below it; `MODEL_API_BASE` carries no
 * trailing slash. A caller that supplies `init` owns the method and the body and inherits the
 * timeout + typed-error discipline unchanged.
 */
export async function modelFetch(
  path: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  init?: RegistryFetchInit,
): Promise<Response> {
  return baseFetch(MODEL_API_BASE, path, timeoutMs, init);
}

/**
 * ➕ WI-V4 — the same bounded, credentialed request against `/api/taxonomy`.
 *
 * ⚠️ **WHY A SECOND BASE RATHER THAN A SECOND TRANSPORT.** The model registry's canonical-fact
 * vocabulary is served from `/api/taxonomy/model-facts`, not from `/api/models`, because it is
 * deployment-wide CK content with no model in it — the register-a-model dialog draws the same four
 * pickers before any model exists, so a `{modelId}`-scoped route could not populate it. That is a
 * different BASE, not a different discipline: same origin, same ADR-043 `{ error, error_args }`
 * envelope, same bounded read timeout, same `ModelApiError`. Every one of the three assumptions the
 * file header names as this transport's preconditions holds, so it rides here rather than growing a
 * fourth near-identical fetch discipline in the tree — which is the exact drift B-3 collapsed.
 */
export async function taxonomyFetch(
  path: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  init?: RegistryFetchInit,
): Promise<Response> {
  return baseFetch(TAXONOMY_API_BASE, path, timeoutMs, init);
}

/**
 * The same bounded, credentialed request against the bare `/api` base, for same-origin routes that
 * hang off no family of their own. The model-set route (`/api/tenant/model-set`) uses it: same origin, same ADR-043 `{ error, error_args }`
 * envelope, same bounded read timeout, same `ModelApiError`. Every precondition in the file header
 * holds, so it rides here rather than growing another near-identical fetch discipline.
 */
export async function apiFetch(
  path: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  init?: RegistryFetchInit,
): Promise<Response> {
  return baseFetch('/api', path, timeoutMs, init);
}

/** The one request implementation both bases share. Private — a caller picks a base by picking a
 * function, so no call site can invent a third base by passing a string. */
async function baseFetch(
  base: string,
  path: string,
  timeoutMs: number,
  init?: RegistryFetchInit,
): Promise<Response> {
  const { signal: caller, ...rest } = init ?? {};
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // ⛔ WIRED BY HAND RATHER THAN WITH `AbortSignal.any`. That helper is not available in every
  // runtime this bundle targets, and it also throws away the one thing the catch below needs:
  // WHICH signal fired. Forwarding the caller's abort into the internal controller keeps ONE signal
  // on the request (so the server still sees the disconnect and terminates the run) while leaving
  // the caller's own signal readable as the discriminator.
  const forward = () => controller.abort();
  caller?.addEventListener('abort', forward);
  if (caller?.aborted) controller.abort();

  try {
    return await fetch(`${base}${path}`, {
      credentials: 'same-origin',
      signal: controller.signal,
      method: 'GET',
      ...rest,
    });
  } catch (e) {
    // A network or abort failure is a LOUD typed error, never a silent empty result. The three
    // known cases are named separately because their remedies differ: a cancel needs no remedy at
    // all, a timeout is worth retrying, a transport failure usually is not.
    if ((e as Error)?.name === 'AbortError') {
      // 🔴 THE CALLER'S SIGNAL IS CHECKED FIRST, AND THE ORDER IS THE WHOLE DISTINCTION. Both can
      // be aborted — a user who cancels a request that was also about to time out — and in that
      // case the CANCEL is the true account: it is the thing the person did, and reporting a
      // timeout would tell them the system gave up on an action they themselves stopped.
      if (caller?.aborted) throw new ModelApiError('request_cancelled', 0);
      throw new ModelApiError('request_timeout', 0);
    }
    throw new ModelApiError('network_error', 0);
  } finally {
    clearTimeout(timer);
    caller?.removeEventListener('abort', forward);
  }
}

/**
 * Raise the backend's closed-vocabulary refusal on a non-2xx, args intact. The raw body never
 * escapes — only the code and the structured args the authored copy interpolates.
 */
export async function throwIfNotOk(resp: Response): Promise<void> {
  if (resp.ok) return;
  let code = 'unknown_error';
  let errorArgs: Record<string, unknown> = {};
  try {
    const body = (await resp.json()) as ApiErrorPayload | null;
    if (body && typeof body.error === 'string') code = body.error;
    if (body && body.error_args && typeof body.error_args === 'object') {
      errorArgs = body.error_args;
    }
  } catch {
    /* non-JSON body — keep the generic code and no args; never surface the raw text */
  }
  throw new ModelApiError(code, resp.status, errorArgs);
}

/**
 * A 2xx whose body IS the result. Use where the caller has something to render or an identifier
 * to take from the response.
 *
 * ⛔ Not interchangeable with `throwIfNotOk`: an empty 2xx here is an ERROR, because coercing it to
 * `{}` hands the caller an object whose required fields are undefined and the failure surfaces
 * three layers later as a blank field rather than here as a fetch error.
 */
export async function jsonOrThrow<T>(resp: Response): Promise<T> {
  await throwIfNotOk(resp);
  const text = await resp.text();
  if (!text) throw new ModelApiError('empty_response_body', resp.status);
  return JSON.parse(text) as T;
}

/** `POST` a JSON body and read the JSON result. */
export async function postJson<T>(
  path: string,
  body: unknown,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<T> {
  return jsonOrThrow<T>(
    await modelFetch(path, timeoutMs, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }),
  );
}

/**
 * `POST` a JSON body and assert success WITHOUT reading a result.
 *
 * ⚠️ THE SPLIT FROM `postJson` IS A CONTRACT DIFFERENCE, NOT SUGAR. A mutation route on this family
 * answers with the refetched row, and `frontend-rules` Persist-Then-Render forbids the CLIENT from
 * drawing it: *"after any mutation, REFETCH from the backend before updating display."* Handing the
 * caller a row invites exactly that render. Routing such a call through `postJson` instead would
 * also make an empty 2xx a spurious `empty_response_body` failure, because a caller with nothing to
 * read has no reason to require a body.
 */
export async function postJsonNoContent(
  path: string,
  body: unknown,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<void> {
  return mutateNoContent('POST', path, body, timeoutMs);
}

/**
 * `PUT` a JSON body and assert success WITHOUT reading a result.
 *
 * ➕ WI-20 — the canonical block's create/edit route (`PUT /api/models/{id}/facts/{factKey}`) has the
 * same contract as its POST siblings: it answers with the refetched BLOCK, and the client must
 * refetch rather than draw it. The verb differs because the route is an idempotent write to a named
 * fact, not a command.
 */
export async function putJsonNoContent(
  path: string,
  body: unknown,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<void> {
  return mutateNoContent('PUT', path, body, timeoutMs);
}

/**
 * `PATCH` a JSON body and assert success WITHOUT reading a result.
 *
 * ➕ **`W-23` (2026-09-05)** — the model issue log's status/assignee write
 * (`PATCH /api/models/{id}/issue-log/{issueUid}`) answers with the refetched ROW, and the same
 * Persist-Then-Render argument the two siblings above are built on applies unchanged: handing the
 * caller that row invites it onto the screen without a read-back.
 *
 * ⚠️ **THE VERB IS THE SERVER'S CHOICE, NOT A STYLE ONE.** The route takes a PARTIAL body
 * (`{ status?, assignee?, reason?, row_version }`) under optimistic concurrency, so it is a patch
 * rather than an idempotent whole-resource `PUT`. A `409` on a stale `row_version` arrives here as
 * an ordinary `ModelApiError` and is the caller's to surface — ⛔ never retried transparently, which
 * would overwrite whatever the other writer just landed.
 */
export async function patchJsonNoContent(
  path: string,
  body: unknown,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<void> {
  return mutateNoContent('PATCH', path, body, timeoutMs);
}

/**
 * The shared body of the three no-content mutators.
 *
 * ⚠️ EXTRACTED when the second verb arrived (WI-20), not before. The two were about to be
 * byte-identical apart from one string, and this family already paid for that: `mdrApi.ts` carried a
 * second copy of this whole transport against the same base, and the two drifted — the model half
 * gained `error_args` and the MDR half did not. One body, one place to fix.
 * ⚠️ The THIRD verb (`PATCH`, `W-23`) cost exactly one member of the union below, which is what the
 * extraction was for.
 */
async function mutateNoContent(
  method: 'POST' | 'PUT' | 'PATCH',
  path: string,
  body: unknown,
  timeoutMs: number,
): Promise<void> {
  await throwIfNotOk(
    await modelFetch(path, timeoutMs, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}
